/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { Settings, Award, User, Menu, X, Sparkles } from 'lucide-react';
import { Logo } from '../ui/Logo';
import { SearchAutocomplete } from '../ui/SearchAutocomplete';
import { LegalDocument } from '../../types';
import { usePermissions } from '../../Hooks/usePermissions';

interface HeaderProps {
  currentView: string;
  setView: (view: string) => void;
  onSearchQuick: (term: string) => void;
  favoritesCount: number;
  currentUser: any;
  onLogout: () => void;
  onLogin: () => void;
  documents?: LegalDocument[];
}

export const Header: React.FC<HeaderProps> = ({
  currentView,
  setView,
  onSearchQuick,
  favoritesCount,
  currentUser,
  onLogout,
  onLogin,
  documents = [],
}) => {
  const [localQuery, setLocalQuery] = React.useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const { hasPermission, hasAnyPermission } = usePermissions();

  const isStaffOrAdmin = 
    currentUser?.role === 'ADMIN' || 
    currentUser?.role === 'STAFF' || 
    (Array.isArray(currentUser?.role) && (currentUser.role.includes('ADMIN') || currentUser.role.includes('STAFF')));

  const canAccessSettings = isStaffOrAdmin || hasPermission('config_access') || hasAnyPermission([
    'view_staff_users', 'view_individual_users', 'view_business_users',
    'create_staff_account', 'activate_account', 'assign_user_group', 'reset_user_password',
    'add_doc', 'edit_doc', 'delete_doc', 'view_stats', 'manage_group_perms',
    'view_logins', 'view_consultations', 'view_downloads', 'view_searches'
  ]);

  const handleNavClick = (view: string) => {
    setView(view);
    setIsMobileMenuOpen(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (localQuery.trim()) {
      onSearchQuick(localQuery);
      setLocalQuery('');
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <header className="sticky top-0 z-50 bg-white/80 border-b border-primary-750/10 shadow-sm backdrop-blur-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center justify-between min-h-[5rem] py-3 gap-y-3 relative">
          
          {/* Logo Brand / Institutional Emblems */}
          <button 
            onClick={() => handleNavClick('home')} 
            className="flex items-center group text-left cursor-pointer focus:outline-none"
            id="header-logo-btn"
          >
            <Logo height={44} className="transition-transform group-hover:scale-[1.01] duration-300" />
          </button>

          {/* Mobile Hamburger Button */}
          <button
            className="md:hidden p-2 text-zinc-600 focus:outline-none cursor-pointer"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>

          {/* Desktop & Mobile Menu wrapper */}
          <div className={`${isMobileMenuOpen ? 'flex flex-col absolute top-[100%] left-0 w-full bg-white border-b border-primary-750/10 shadow-lg p-4 gap-4 z-50' : 'hidden md:flex items-center gap-4 flex-1 justify-end'}`}>

            {/* Quick inline search with autocomplete (hidden on HomeView, SearchView, and DocumentDetailView) */}
            {/* {currentView !== 'home' && currentView !== 'search' && !currentView.includes('document') && (
              <div className={`relative z-50 ${isMobileMenuOpen ? 'w-full' : 'w-64 lg:w-80'}`}>
                <SearchAutocomplete
                  id="header-search-bar"
                  value={localQuery}
                  onChange={setLocalQuery}
                  onSearch={(term) => {
                    onSearchQuick(term);
                    setLocalQuery('');
                    setIsMobileMenuOpen(false);
                  }}
                  documents={documents}
                  placeholder="Rechercher un texte de loi..."
                  variant="header"
                />
              </div>
            )} */}

            {/* Core Navigation Tabs */}
            <nav className={`flex font-medium text-[11px] text-zinc-600 uppercase tracking-widest ${isMobileMenuOpen ? 'flex-col gap-2 w-full' : 'items-center gap-1 sm:gap-2'}`}>
              <button
                onClick={() => handleNavClick('home')}
                className={`px-3 py-2 md:py-1.5 rounded-full text-left md:text-center cursor-pointer transition-colors ${currentView === 'home' ? 'text-violet-medium font-bold bg-violet-medium/10' : 'hover:text-zinc-900 hover:bg-zinc-100/50'}`}
              >
                Accueil
              </button>
              <button
                onClick={() => handleNavClick('categories')}
                className={`px-3 py-2 md:py-1.5 rounded-full text-left md:text-center cursor-pointer transition-colors ${currentView === 'categories' ? 'text-violet-medium font-bold bg-violet-medium/10' : 'hover:text-zinc-900 hover:bg-zinc-100/50'}`}
              >
                Catégories
              </button>
              <button
                onClick={() => handleNavClick('search')}
                className={`px-3 py-2 md:py-1.5 rounded-full text-left md:text-center cursor-pointer transition-colors ${currentView === 'search' ? 'text-violet-medium font-bold bg-violet-medium/10' : 'hover:text-zinc-900 hover:bg-zinc-100/50'}`}
              >
                Recherche
              </button>
              {currentUser && (
                <button
                  onClick={() => handleNavClick('dashboard')}
                  className={`px-3 py-2 md:py-1.5 rounded-full text-left md:text-center cursor-pointer transition-colors flex items-center gap-1 ${currentView === 'dashboard' ? 'text-violet-medium font-bold bg-violet-medium/10' : 'hover:text-zinc-900 hover:bg-zinc-100/50'}`}
                >
                  Mon espace
                  {favoritesCount > 0 && (
                    <span className="bg-violet-medium text-white font-mono text-[9px] rounded-full w-4 h-4 flex items-center justify-center font-bold">
                      {favoritesCount}
                    </span>
                  )}
                </button>
              )}
              {(hasPermission('view_formulas') || isStaffOrAdmin || (currentUser && (currentUser.permissions?.includes('view_formulas') || true))) && (
                <button
                  onClick={() => handleNavClick('formulas')}
                  className={`px-3 py-2 md:py-1.5 rounded-full text-left md:text-center cursor-pointer transition-colors flex items-center gap-1.5 ${currentView === 'formules' ? 'text-violet-medium font-bold bg-violet-medium/10' : 'hover:text-zinc-900 hover:bg-zinc-100/50'}`}
                >
                  <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                  <span>Formules</span>
                </button>
              )}
            </nav>

            {/* User Section & Admin Access */}
            <div className={`flex gap-2 ${isMobileMenuOpen ? 'flex-col items-start w-full mt-2 pt-4 border-t border-zinc-100' : 'items-center'}`}>

              {currentUser && canAccessSettings && (
                <button
                  onClick={() => handleNavClick('admin')}
                  className={`flex items-center gap-1.5 px-3 py-2 md:py-1.5 rounded-full text-zinc-500 hover:text-zinc-800 hover:bg-zinc-100 cursor-pointer text-[10px] uppercase font-bold tracking-wider ${currentView === 'admin' ? 'bg-violet-medium/10 text-violet-medium' : ''}`}
                  title="Portail Administration"
                >
                  <Settings className="w-4 h-4" />
                  <span>Paramètres</span>
                </button>
              )}

              {currentUser ? (
                <div className={`flex gap-2 ${isMobileMenuOpen ? 'flex-col items-start w-full' : 'items-center'}`}>
                  <div className={`flex flex-col leading-none ${isMobileMenuOpen ? 'text-left mb-2 px-3' : 'text-right mr-1 hidden lg:flex'}`}>
                    <span className="text-xs font-bold text-zinc-900">{currentUser.fullName}</span>
                    <span className="text-[9px] text-orange-light tracking-wider font-semibold uppercase flex items-center gap-0.5 mt-1 md:justify-end">
                      <Award className="w-2.5 h-2.5" /> {(Array.isArray(currentUser.role) ? currentUser.role.includes('ADMIN') : currentUser.role === 'ADMIN') ? 'Admin' : currentUser.cabinetName}
                    </span>
                  </div>
                  <button
                    onClick={() => { onLogout(); setIsMobileMenuOpen(false); }}
                    className="px-3 py-2 md:py-1.5 border border-zinc-200 hover:border-red-200 text-zinc-500 hover:text-red-650 rounded-full text-[10px] uppercase font-bold tracking-wider cursor-pointer transition-colors"
                  >
                    Déconnexion
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => { onLogin(); setIsMobileMenuOpen(false); }}
                    className="px-3.5 py-2 md:py-1.5 border border-zinc-200 hover:border-violet-300 text-zinc-700 hover:text-violet-800 rounded-full text-[10px] uppercase font-bold tracking-wider cursor-pointer transition-colors bg-white"
                  >
                    Se connecter
                  </button>
                  <button
                    onClick={() => handleNavClick('register')}
                    className="px-4 py-2 md:py-1.5 bg-violet-medium hover:bg-violet-dark text-white rounded-full text-[10px] uppercase font-bold tracking-wider cursor-pointer transition-colors border-none shadow-sm"
                  >
                    Créer un compte
                  </button>
                </div>
              )}

              <div className="hidden md:flex bg-zinc-50 text-zinc-800 p-1.5 rounded-full border border-zinc-200">
                <User className="w-3.5 h-3.5" />
              </div>
            </div>

          </div>
        </div>
      </div>
    </header>
  );
};
